import { useState } from 'react';
import Dialog from 'components/Dialog';
import liff from '@line/liff';
import { useNavigate } from 'react-router';
import CouponTutorialImg from 'assets/coupon-tutorial.jpg';
import { LINE_OFFICIAL_URL, LIFF_COUPON_CLAIM_URL } from 'constants/externalLinks';

interface Props {
  visible: boolean;
  // PG-92 r3: 歸還剛建好的 user_rent_log_id,用 LIFF URL query 帶進 LINE webview
  // 取代 localStorage(LINE webview 與 Safari 不互通,會害登入者領不到券)。
  // PG-56 新版後端改用 claim_token(uuid 字串)
  rentLogId?: string | number | null;
  // PG-186 全家:交件確認(日翊 B02)後才可領券。全家完成時不當場領券,改顯示「2-3 天後 LINE OA 領取」引導。
  famiMode?: boolean;
  onClose: () => void;
}

/**
 * 歸還成功後的共用彈窗（momo 免登入 / 會員登入兩條路線共用）：
 * 不在當下直接給使用者領券清單（舊行為），改成引導到 /discount/ecommerce 領取。
 */
const ReturnCompleteDialog = ({ visible, rentLogId, famiMode, onClose }: Props) => {
  const navigate = useNavigate();
  const [confirmDeclineVisible, setConfirmDeclineVisible] = useState(false);

  // PG-186 全家:前往 P+ LINE OA(交件確認後 2-3 天在這領券),不當場領。
  const goLineOA = () => {
    window.location.href = LINE_OFFICIAL_URL;
    setTimeout(() => navigate('/rent', { replace: true }), 800);
  };
  const closeFami = () => {
    try {
      if (liff.isInClient && liff.isInClient()) {
        liff.closeWindow();
        return;
      }
    } catch (e) {
      // ignore
    }
    onClose();
    navigate('/rent');
  };

  /**
   * 「領取優惠券」：
   * 有 rentLogId → 直接跳 LIFF URL 並用 query 帶 user_rent_log_id,
   * /discount/ecommerce 進去後會優先讀 query → 後端綁定可領券。
   * 沒 rentLogId → fallback 跳 OA 首頁讓使用者走 rich menu 進 LIFF
   * (代價:跨 webview localStorage 讀不到,可能領不到券,但至少能到 LINE)。
   */
  const goClaimCoupon = () => {
    const url = rentLogId
      ? `${LIFF_COUPON_CLAIM_URL}?user_rent_log_id=${rentLogId}`
      : LINE_OFFICIAL_URL;
    window.location.href = url;
    // 跳 LINE 後底層導回 /rent,避免使用者切回 tab 還停在 /return 中繼頁
    setTimeout(() => {
      navigate('/rent', { replace: true });
    }, 800);
  };

  // 「不領取優惠券」：舊文案為「稍後再說」，誤導使用者以為晚點還能回來領。
  // 對 DDC 流程來說，包裝編號一但掃描過就無法再領，所以要求二次確認並明確用字。
  const handleDecline = () => {
    setConfirmDeclineVisible(false);
    try {
      if (liff.isInClient && liff.isInClient()) {
        liff.closeWindow();
        return;
      }
    } catch (e) {
      // ignore
    }
    onClose();
    navigate('/rent');
  };

  return (
    <>
    <Dialog
      visible={visible}
      onConfirm={onClose}
      showOk={false}
      message={
        // PG-90: iOS Safari 上彈窗總高超過視窗,加 maxHeight + overflowY 讓內容可捲動,
        // 避免 Lynn 04/27 回報的「您有專屬優惠券待領取」h5 在頂部被切掉。
        // 用 --vh (return.tsx 設定) 對齊 iOS 浮動工具列實際可視高,避免 100vh 不準。
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            padding: '8px 4px 4px',
            maxHeight: 'calc(var(--vh, 1vh) * 78)',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {famiMode ? (
            <>
              <h5 style={{ margin: 0, color: '#3db270', fontSize: 16, fontWeight: 700 }}>
                交件完成！
              </h5>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#303030', textAlign: 'center', lineHeight: 1.5 }}>
                循環包裝已交寄全家 🎉
              </p>
              {/* TODO 素材:換成 P+ LINE OA「領取優惠券」入口截圖(chris/Anna 提供),先用教學圖 placeholder */}
              <img
                src={CouponTutorialImg}
                alt="LINE OA 領券入口(示意)"
                style={{ width: '100%', maxWidth: 240, borderRadius: 8, border: '1px solid #ebebeb' }}
              />
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#3db270', textAlign: 'center', lineHeight: 1.5, maxWidth: 250 }}>
                ✅ 系統確認交件後約 2-3 個工作天,優惠券會發放
                <br />
                可至配客嘉 LINE 領取
              </p>
              <button
                type="button"
                onClick={goLineOA}
                style={{ width: 220, height: 46, background: '#3db270', boxShadow: '6px 8px 0px #00000029', border: '1px solid #303030', borderRadius: 6, color: '#ffffff', fontSize: 17, fontWeight: 700, cursor: 'pointer', marginTop: 4 }}
              >
                前往LINE領券
              </button>
              <button
                type="button"
                onClick={closeFami}
                style={{ background: 'none', border: 'none', color: '#9a9a9a', fontSize: 13, cursor: 'pointer', padding: '4px 8px', marginTop: 2, textDecoration: 'underline' }}
              >
                我知道了
              </button>
            </>
          ) : (
            <>
          <h5 style={{ margin: 0, color: '#3db270', fontSize: 16, fontWeight: 700 }}>
            歸還完成！
          </h5>

          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 600,
              color: '#303030',
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            您有專屬優惠券待領取 🎁
          </p>

          <p
            style={{
              margin: 0,
              fontSize: 12,
              color: '#77777A',
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            下方點擊「領取優惠券」
          </p>

          <img
            src={CouponTutorialImg}
            alt="coupon tutorial"
            style={{
              width: '100%',
              maxWidth: 240,
              borderRadius: 8,
              border: '1px solid #ebebeb',
            }}
          />

          <button
            type="button"
            onClick={goClaimCoupon}
            style={{
              width: 220,
              height: 46,
              background: '#3db270',
              boxShadow: '6px 8px 0px #00000029',
              border: '1px solid #303030',
              borderRadius: 6,
              color: '#ffffff',
              fontSize: 17,
              fontWeight: 700,
              cursor: 'pointer',
              marginTop: 4,
            }}
          >
            領取優惠券
          </button>

          {/* UX 引導：使用者離開外部瀏覽器進 LINE app 時，iOS 會跳
              「在 LINE 中開啟？」prompt。若按允許走 LIFF (native LINE session) 可免輸入帳密；
              若取消則掉到 LINE 網頁登入 + 2FA，卡在 LINE 聊天列表。 */}
          <p
            style={{
              margin: '-2px 0 0',
              fontSize: 11,
              color: '#9a9a9a',
              textAlign: 'center',
              lineHeight: 1.45,
              maxWidth: 240,
            }}
          >
            💡 點擊後請選擇「在 LINE 中開啟」
            <br />
            即可自動登入，不需輸入帳密
          </p>

          <button
            type="button"
            onClick={() => setConfirmDeclineVisible(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#9a9a9a',
              fontSize: 13,
              cursor: 'pointer',
              padding: '4px 8px',
              marginTop: 2,
              textDecoration: 'underline',
            }}
          >
            不領取優惠券
          </button>
            </>
          )}
        </div>
      }
    />

    {/* 二次確認：防止誤按「不領取優惠券」。DDC 流程不會再給第二次機會。 */}
    <Dialog
      visible={confirmDeclineVisible}
      onConfirm={handleDecline}
      onCancel={() => setConfirmDeclineVisible(false)}
      cancelable={true}
      okText="確定不領取"
      cancelText="返回"
      message={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            padding: '4px',
            textAlign: 'center',
          }}
        >
          <h5 style={{ margin: 0, color: '#303030', fontSize: 16, fontWeight: 700 }}>
            確定不領取優惠券？
          </h5>
          <p style={{ margin: 0, fontSize: 13, color: '#77777A', lineHeight: 1.5 }}>
            本次歸還的優惠券將無法再領取，
            <br />
            確定要放棄嗎？
          </p>
        </div>
      }
    />
    </>
  );
};

export default ReturnCompleteDialog;
